import { faCheckCircle } from "@fortawesome/free-regular-svg-icons";
import {
  faArrowLeft,
  faExclamationCircle,
  faMinusCircle,
  faPencil,
  faPlus,
  faStopCircle,
  faTimes,
  faWarning,
  faWrench,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card } from "@ventumsoftware/react-toolkit";
import React, { type FC, Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useApi, useFetchedResource } from "../../api/APIContext";
import { useApiCall } from "../../api/api";
import type { Company, User } from "../../api/dto";
import { UserPermissions, type UserPermissionsSettings } from "../../api/dto/UserPermissions";
import { Callout } from "../../components/content/Callout";
import { ContentContainer } from "../../components/content/ContentContainer";
import { PageHeader } from "../../components/content/PageHeader";
import { SectionHeader } from "../../components/content/SectionHeader";
import { Badge, BadgeStyle } from "../../components/data/Badge";
import { DisplayModeToggle } from "../../components/data/DisplayModeToggle";
import { Toggle } from "../../components/form/Toggle";
import { useModal } from "../../components/layout/ModalProvider";
import { useRefresh, useRefreshEffect } from "../../components/RefreshController";
import { DeleteUserModal } from "../../modals/DeleteUserModal";
import { EditUserModal } from "../../modals/EditUserModal";
import { GrantCompanyAccessModal } from "../../modals/GrantCompanyAccessModal";
import { ManageUserLoginModal } from "../../modals/ManageUserLoginModal";
import { ResetUserPasswordModal } from "../../modals/ResetUserPasswordModal";
import { RevokeCompanyAccessModal } from "../../modals/RevokeCompanyAccessModal";

export const SettingsUserDetailsPage: FC<{}> = (props) => {
  const { userId } = useParams();
  const { users, loading, refresh } = useApi();
  const navigate = useNavigate();
  const user = users.find((u) => u.id === userId);
  if (!user) {
    return (
      <ContentContainer>
        <PageHeader>Gebruiker beheren</PageHeader>
        <div className={"mb-4"}>Gebruiker niet gevonden.</div>
        <Button
          type={"primary"}
          icon={faArrowLeft}
          size={"md"}
          text={"Terug naar overzicht"}
          onClick={() => navigate("/settings/users")}
        />
      </ContentContainer>
    );
  }
  return (
    <ContentContainer>
      <Button
        type={"blank"}
        icon={faArrowLeft}
        size={"md"}
        text={"Terug naar overzicht"}
        onClick={() => navigate("/settings/users")}
      />
      <PageHeader>Gebruiker beheren</PageHeader>
      <div className="flex flex-col space-y-6">
        <ManageUserCard user={user} />
        {!user.isAdmin && <ManageUserCompanyPermissionsCard user={user} />}
      </div>
    </ContentContainer>
  );
};

const ManageUserCard: FC<{ user: User }> = (props) => {
  const editUser = useModal({
    title: "Pas gebruiker aan",
    body: <EditUserModal user={props.user} />,
    size: "xl",
  });
  const editUserPassword = useModal({
    title: "Wachtwoord reset",
    body: <ResetUserPasswordModal user={props.user} />,
    size: "md",
  });
  const manageUserLogin = useModal({
    title: props.user.enabled ? "Login intrekken" : "Login activeren",
    body: <ManageUserLoginModal user={props.user} />,
    size: "md",
  });
  const delUser = useModal({
    title: "Gebruiker verwijderen",
    body: <DeleteUserModal user={props.user} />,
    size: "md",
  });
  return (
    <Card title={"Details"}>
      <div className={"flex items-start justify-between"}>
        <div className={"flex-1 mr-6"}>
          <table>
            <tbody>
              <tr>
                <td className={"text-sm font-medium text-slate-600 py-1 pr-4"}>Voornaam</td>
                <td>{props.user.first_name}</td>
              </tr>
              <tr>
                <td className={"text-sm font-medium text-slate-600 py-1 pr-4"}>Achternaam</td>
                <td>{props.user.last_name}</td>
              </tr>
              <tr>
                <td className={"text-sm font-medium text-slate-600 py-1 pr-4"}>E-mail</td>
                <td>{props.user.email}</td>
              </tr>
            </tbody>
          </table>
          <div>
            {props.user.isAdmin && (
              <Callout color={"success"}>
                <SectionHeader>
                  <FontAwesomeIcon icon={faWrench} className={"mr-2 text-base"} />
                  Administrator
                </SectionHeader>
                <p className={"text-sm"}>
                  Dit is een administrator. Dit houdt in dat deze gebruiker overal bij kan, en alles mag.
                </p>
              </Callout>
            )}
            {props.user.isApiUser && (
              <Callout color={"primary"}>
                <SectionHeader>
                  <FontAwesomeIcon icon={faWrench} className={"mr-2 text-base"} />
                  Systeem Koppeling
                </SectionHeader>
                <p className={"text-sm"}>
                  Deze gebruiker wordt door een systeem gebruikt om een koppeling te maken met deze app.
                </p>
              </Callout>
            )}
            {!props.user.enabled && (
              <Callout color={"warning"}>
                <SectionHeader>
                  <FontAwesomeIcon icon={faWarning} className={"mr-2 text-base"} />
                  Inloggen uitgeschakeld
                </SectionHeader>
                <p className={"text-sm mb-4"}>
                  Met deze gebruiker kan momenteel niet ingelogd worden omdat dit staat uitgeschakeld. Indien gewenst is
                  het mogelijk om deze weer te activeren.
                </p>
                <Button
                  type={"primary"}
                  size={"md"}
                  text={"Login activeren"}
                  icon={faCheckCircle}
                  onClick={() => manageUserLogin.open()}
                />
              </Callout>
            )}
          </div>
        </div>
        <div className={"flex flex-col items-start space-y-2"}>
          <Button
            type={"secondary"}
            size={"md"}
            text={"Gegevens aanpassen"}
            icon={faPencil}
            onClick={() => editUser.open()}
          />
          <Button
            type={"secondary"}
            size={"md"}
            text={"Wachtwoord reset"}
            icon={faWarning}
            onClick={() => editUserPassword.open()}
          />
          <Button
            type={"secondary"}
            size={"md"}
            text={props.user.enabled ? "Login intrekken" : "Login activeren"}
            icon={faWarning}
            onClick={() => manageUserLogin.open()}
          />
          <Button
            type={"danger"}
            size={"md"}
            text={"Permanent verwijderen"}
            icon={faWarning}
            onClick={() => delUser.open()}
          />
        </div>
      </div>
    </Card>
  );
};

const ManageUserCompanyPermissionsCard: FC<{ user: User }> = (props) => {
  const { companies } = useApi();
  const { listCompanyPermissionsForUser } = useApiCall();
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const companyPermissions = useFetchedResource((userId: string) => listCompanyPermissionsForUser(userId));
  useRefreshEffect(() => {
    companyPermissions.reload(props.user.id);
  }, [props.user.id]);
  const grantAccessModal = useModal({
    title: "Toegang verlenen",
    body: <GrantCompanyAccessModal user={props.user} onFinished={(c) => (c ? setSelectedCompany(c) : null)} />,
  });

  const company = companies.find((c) => c.id === selectedCompany);
  const userPermissions = companyPermissions.resource?.find((cp) => cp.companyId === selectedCompany);

  return (
    <Card title={"Rechten op bedrijven"}>
      <hr className={"-mx-6 border-slate-200 mt-6"} />
      <div className={"flex items-stretch"}>
        <div className={"w-96 pr-4 py-3 border-r border-slate-200 space-y-3"}>
          {companyPermissions.resource?.length === 0 && (
            <div className={"text-center text-slate-600 text-sm my-8"}>
              Deze gebruiker heeft geen directe toegang op bedrijven.
            </div>
          )}

          {companyPermissions.resource?.map((cp, i) => {
            const company = companies.find((c) => c.id === cp.companyId);
            if (!company) {
              return <Fragment key={i}></Fragment>;
            }
            return (
              <div
                className={`rounded-lg ${cp.companyId === selectedCompany ? "border-2 border-brand-600 bg-brand-50" : "border-2 border-slate-200"} px-4 py-3 cursor-default hover:bg-brand-50 hover:border-brand-200`}
                key={i}
                onClick={() => setSelectedCompany(cp.companyId)}
              >
                <div className={"flex items-center justify-between"}>
                  <div className={"text-sm font-medium"}>{company.name}</div>
                  {cp.hasExplicitAccess ? (
                    <Badge style={BadgeStyle.info}>Direct</Badge>
                  ) : (
                    <Badge style={BadgeStyle.default}>Indirect</Badge>
                  )}
                </div>
                <div className={"text-xs text-slate-600 mt-2"}>
                  {cp.hasExplicitAccess ? (
                    <>De gebruiker heeft toegang tot dit bedrijf omdat het expliciet is ingesteld.</>
                  ) : (
                    <>De gebruiker heeft toegang tot dit bedrijf als gevolg van toegang op een hoger niveau.</>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className={"flex-1 pl-4 py-3"}>
          {company && userPermissions && (
            <ManageUserCompanyPermissionsCardEditor company={company} userPermissions={userPermissions} />
          )}
        </div>
      </div>
      <hr className={"-mx-6 border-slate-200 mb-4"} />
      <Button
        type={"secondary"}
        size={"md"}
        text={"Toegang tot bedrijf verlenen"}
        icon={faPlus}
        onClick={() => grantAccessModal.open()}
      />
    </Card>
  );
};
const ManageUserCompanyPermissionsCardEditor: FC<{
  company: Company;
  userPermissions: UserPermissions;
}> = (props) => {
  const revokeModal = useModal({
    title: "Toegang ontzeggen",
    body: <RevokeCompanyAccessModal company={props.company} userId={props.userPermissions.userId} />,
  });
  const [settings, setSettings] = useState<UserPermissionsSettings>(props.userPermissions);
  const { grantCompanyAccessToUser } = useApiCall();
  const refresh = useRefresh();
  useEffect(() => {
    setSettings(props.userPermissions);
  }, [props.userPermissions]);
  const save = async () => {
    await grantCompanyAccessToUser(props.userPermissions.userId, props.userPermissions.companyId, settings);
    refresh();
  };
  return (
    <div className={""}>
      <div className={"flex items-center justify-between"}>
        <h2 className={"text-lg font-bold"}>{props.company.name}</h2>
        {props.userPermissions.hasExplicitAccess ? (
          <>
            <Button
              type={"danger"}
              size={"md"}
              text={"Toegang ontzeggen"}
              icon={faMinusCircle}
              onClick={() => revokeModal.open()}
            />
          </>
        ) : (
          <></>
        )}
      </div>
      {!props.userPermissions.hasExplicitAccess ? (
        <>
          <Callout color={"primary"}>
            <SectionHeader>Indirecte toegang</SectionHeader>
            <p className={"text-sm -mt-2 mb-2"}>
              De gebruiker heeft toegang tot dit bedrijf als gevolg van toegang op een hoger niveau.
            </p>
            <p className={"text-sm mb-4"}>
              Dit betekent dat alle rechten worden overgenomen van dat hogere niveau. Als je hier meer of minder rechten
              wilt instellen kun je expliciet rechten overschrijven voor dit bedrijf, en onderliggende.
            </p>
            <Button type={"primary"} size={"sm"} text={"Directe toegang instellen"} onClick={() => save()} />
          </Callout>
        </>
      ) : (
        <></>
      )}
      <div className={"relative"}>
        {!props.userPermissions.hasExplicitAccess ? (
          <>
            <div className={"absolute z-10 inset-0 bg-[#fffa] grayscale cursor-not-allowed"}></div>
          </>
        ) : (
          <></>
        )}
        <div className={"space-y-2 my-4"}>
          <Toggle
            label={"Inspecties/CompetencyTests uitvoeren"}
            value={settings.canExecuteAssessments}
            onChange={(v) => setSettings((old) => ({ ...old, canExecuteAssessments: v }))}
          />
          <Toggle
            label={"Inspecties/CompetencyTests exporteren"}
            value={settings.canExportAssessments}
            onChange={(v) => setSettings((old) => ({ ...old, canExportAssessments: v }))}
          />
          <Toggle
            label={"Alle inspecties/CompetencyTests inzien"}
            value={settings.canAccessAllAssessments}
            onChange={(v) => setSettings((old) => ({ ...old, canAccessAllAssessments: v }))}
          />
          <Toggle
            label={"Geschiedenis van inspecties/CompetencyTests inzien"}
            value={settings.canAccessAssessmentHistory}
            onChange={(v) => setSettings((old) => ({ ...old, canAccessAssessmentHistory: v }))}
          />
          <Toggle
            label={"Bedrijven beheren"}
            value={settings.canManageCompanies}
            onChange={(v) => setSettings((old) => ({ ...old, canManageCompanies: v }))}
          />
          <Toggle
            label={"Bedrijven beheren (destructief)"}
            value={settings.canManageCompaniesDestructive}
            onChange={(v) => setSettings((old) => ({ ...old, canManageCompaniesDestructive: v }))}
          />
          <Toggle
            label={"Projecten beheren"}
            value={settings.canManageProjects}
            onChange={(v) => setSettings((old) => ({ ...old, canManageProjects: v }))}
          />
          <Toggle
            label={"Projecten beheren (destructief)"}
            value={settings.canManageProjectsDestructive}
            onChange={(v) => setSettings((old) => ({ ...old, canManageProjectsDestructive: v }))}
          />
          <Toggle
            label={"Inspecties/CompetencyTests beheren"}
            value={settings.canManageAssessments}
            onChange={(v) => setSettings((old) => ({ ...old, canManageAssessments: v }))}
          />
          <Toggle
            label={"Inspecties/CompetencyTests beheren (destructief)"}
            value={settings.canManageAssessmentsDestructive}
            onChange={(v) => setSettings((old) => ({ ...old, canManageAssessmentsDestructive: v }))}
          />
          <Toggle
            label={"Certificaten inzien"}
            value={settings.canAccessCertificates}
            onChange={(v) => setSettings((old) => ({ ...old, canAccessCertificates: v }))}
          />
          <Toggle
            label={"Dashboards inzien"}
            value={settings.canAccessDashboards}
            onChange={(v) => setSettings((old) => ({ ...old, canAccessDashboards: v }))}
          />
          <Toggle
            label={"Toegang tot web"}
            value={settings.canAccessWeb}
            onChange={(v) => setSettings((old) => ({ ...old, canAccessWeb: v }))}
          />
          <Toggle
            label={"Toegang tot mobiel"}
            value={settings.canAccessMobile}
            onChange={(v) => setSettings((old) => ({ ...old, canAccessMobile: v }))}
          />
          <Toggle
            label={"Importeren"}
            value={settings.canImport}
            onChange={(v) => setSettings((old) => ({ ...old, canImport: v }))}
          />
          <Toggle
            label={"Gebruikers beheren"}
            value={settings.canManageUsers}
            onChange={(v) => setSettings((old) => ({ ...old, canManageUsers: v }))}
          />
          <Toggle
            label={"Checklisten beheren"}
            value={settings.canManageChecklists}
            onChange={(v) => setSettings((old) => ({ ...old, canManageChecklists: v }))}
          />
        </div>
        <div className={"grid grid-cols-2 gap-2 my-4"}>
          <Toggle
            label={"Flens Competency Tests"}
            value={settings.accessibleQuestionSetTypes.includes("Fct")}
            onChange={(v) =>
              setSettings((old) => ({
                ...old,
                accessibleQuestionSetTypes: v
                  ? [...old.accessibleQuestionSetTypes, "Fct"]
                  : old.accessibleQuestionSetTypes.filter((x) => x !== "Fct"),
              }))
            }
          />
          <Toggle
            label={"Rigger Competency Tests"}
            value={settings.accessibleQuestionSetTypes.includes("Rct")}
            onChange={(v) =>
              setSettings((old) => ({
                ...old,
                accessibleQuestionSetTypes: v
                  ? [...old.accessibleQuestionSetTypes, "Rct"]
                  : old.accessibleQuestionSetTypes.filter((x) => x !== "Rct"),
              }))
            }
          />
          <Toggle
            label={"Flens Inspection Reports"}
            value={settings.accessibleQuestionSetTypes.includes("Fir")}
            onChange={(v) =>
              setSettings((old) => ({
                ...old,
                accessibleQuestionSetTypes: v
                  ? [...old.accessibleQuestionSetTypes, "Fir"]
                  : old.accessibleQuestionSetTypes.filter((x) => x !== "Fir"),
              }))
            }
          />
          <Toggle
            label={"Lifting Inspection Reports"}
            value={settings.accessibleQuestionSetTypes.includes("Lir")}
            onChange={(v) =>
              setSettings((old) => ({
                ...old,
                accessibleQuestionSetTypes: v
                  ? [...old.accessibleQuestionSetTypes, "Lir"]
                  : old.accessibleQuestionSetTypes.filter((x) => x !== "Lir"),
              }))
            }
          />
        </div>
        <Button
          type={"primary"}
          size={"md"}
          disabled={settings instanceof UserPermissions}
          text={"Wijzigingen opslaan"}
          onClick={() => save()}
        />
      </div>
    </div>
  );
};
